<template>
  <div
    class="flex grow justify-center px-4"
    role="region"
    aria-label="Verification code entry"
  >
    <template v-if="step === 'contact'">
      <slot />
    </template>
    <template v-else-if="step === 'code'">
      <div
        class="flex w-full max-w-lg flex-col items-center space-y-7"
        :class="!showInDrawer ? 'justify-center' : ''"
      >
        <a
          v-if="!showInDrawer"
          class="text-grays-darkest cursor-pointer self-start"
          @click="step = 'contact'"
          href="#"
          role="button"
          aria-label="Go back to previous step"
          >&larr; Back</a
        >
        <h1 class="text-grays-darkest text-2xl font-semibold" id="otp-title">
          Enter verification code
        </h1>
        <p
          class="text-grays-darkest flex flex-col items-center text-center sm:block"
          id="otp-description"
        >
          <span class="inline">Please enter the 4-digit code sent to</span>
          <span class="py-1 font-semibold sm:px-1 sm:py-0">{{
            formattedContact?.formatted || contact
          }}</span>
          <span class="inline">{{
            isCheckout
              ? 'to complete your checkout.'
              : 'to verify your account.'
          }}</span>
        </p>
        <div
          class="relative flex justify-center"
          :class="showInDrawer ? 'gap-2' : 'gap-4 sm:gap-4'"
          role="group"
          aria-labelledby="otp-title"
          aria-describedby="otp-description"
        >
          <input
            ref="hiddenInput"
            data-testid="otp-input"
            v-model="code"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            autocomplete="one-time-code"
            class="fixed left-0 top-0 z-[-1] h-[100vh] w-[100vw] opacity-0"
            @input="handleInput"
            @focus="handleFocus"
            @blur="isInputFocused = false"
            @paste="handlePaste"
            aria-label="Enter verification code"
            :aria-invalid="codeError"
            :aria-errormessage="codeError ? 'error-message' : undefined"
          />
          <div
            v-for="(digit, index) in displayDigits"
            :data-testid="`otp-digit-${index}`"
            :key="index"
            class="bg-grays-lighter focus:border-grays-dark border-grays-lighter disabled:text-grays-darkest text-grays-darkest relative inline-flex h-14 w-14 items-center justify-center rounded-lg text-2xl sm:h-20 sm:w-20 sm:text-3xl"
            :class="{
              'border-2 border-red-500': codeError,
              'border-grays-dark border-2':
                focusedIndex === index && !codeError,
              'cursor-text': focusedIndex === index,
              'cursor-default': focusedIndex !== index,
            }"
            @click="focusHiddenInput"
            role="presentation"
          >
            <div class="relative flex h-8 items-center justify-center">
              <span class="absolute" aria-hidden="true">{{ digit }}</span>
              <div
                v-if="focusedIndex === index && isInputFocused"
                class="bg-grays-dark cursor-animation absolute h-8 w-0.5"
                aria-hidden="true"
              ></div>
            </div>
          </div>
        </div>
        <span
          v-if="codeError"
          class="text-red-medium"
          role="alert"
          id="error-message"
          >The code entered is incorrect. Please check your code and try
          again.</span
        >
        <ZnButton
          :disabled="code.length !== 4"
          :class="`w-full text-center ${
            code.length !== 4 ? 'bg-grays-light text-white' : ''
          }`"
          @click="handleVerify()"
          :aria-label="
            loading || forceLoading ? 'Verifying code...' : 'Verify code'
          "
        >
          <span v-if="!(loading || forceLoading)">Verify code</span>
          <ZenniIconsIconLoading v-else class="text-white" aria-hidden="true" />
        </ZnButton>
        <div class="h-12">
          <span class="text-grays-darkest">
            Didn't get a code?
            <span
              v-if="timeout !== null"
              class="text-grays-dark"
              aria-live="polite"
            >
              Resend in :{{ timeout }}
            </span>
            <button
              v-else
              type="button"
              class="text-grays-darkest underline underline-offset-2"
              @click="handleSendCode()"
              :disabled="loading"
            >
              Resend
            </button>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { useOtpRepository } from '@/composables/api/repositories/otp'
import { type OtpLoginResBody } from '@/types/magicLink'
import { toast } from '@/components/Toast'
import { ref, toRefs, computed, watch, onUnmounted, nextTick } from '#imports'

const emit = defineEmits<{
  (event: 'verified', data: OtpLoginResBody): void
}>()

const props = defineProps({
  contact: {
    type: String,
    required: false,
    default: () => '',
  },
  canUsePhone: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  forceLoading: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  showInDrawer: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  storeId: {
    type: String,
    required: false,
    default: undefined,
  },
  isCheckout: {
    type: Boolean,
    required: false,
    default: () => false,
  },
})

const { forceLoading } = toRefs(props)
const timeoutDuration = 60
const codeError = ref<boolean>(false)
const timeout = ref<number | null>(null)
const timer = ref<ReturnType<typeof setInterval> | null>(null)
const step = ref<'contact' | 'code'>('contact')
const loading = ref<boolean>(false)

const isPossibleNumber = computed<boolean>(() => {
  if (!props.canUsePhone) {
    return false
  }

  if (props.contact.includes('@')) {
    return false
  }

  const phoneNumber = parsePhoneNumberFromString(props.contact, 'US')

  return phoneNumber?.isPossible() ?? false
})

const formattedContact = computed<{ formatted: string; e164: string } | null>(
  () => {
    if (isPossibleNumber.value) {
      const parsedPhone = parsePhoneNumberFromString(props.contact, 'US')

      if (parsedPhone) {
        const formatted = parsedPhone.formatNational()
        const e164 = parsedPhone.format('E.164')

        return {
          formatted,
          e164,
        }
      }
    }

    return null
  },
)

const isValidContact = computed(() => {
  if (isPossibleNumber.value) {
    const parsedPhone = parsePhoneNumberFromString(props.contact, 'US')
    return parsedPhone?.isValid() ?? false
  } else {
    // Simple email regex - checks for @, domain, and TLD
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(props.contact)
  }
})

const clearTimer = () => {
  if (timer.value !== null) {
    clearInterval(timer.value)
    timer.value = null
  }
}

const startTimer = () => {
  clearTimer()
  timer.value = setInterval(() => {
    if (timeout.value !== null && timeout.value > 1) {
      timeout.value -= 1
    } else {
      timeout.value = null
      clearTimer()
    }
  }, 1000)
}

const otpApi = useOtpRepository()

const code = ref<string>('')
const hiddenInput = ref<HTMLInputElement | null>(null)
const focusedIndex = computed(() => code.value.length)

const displayDigits = computed(() => {
  const digits = code.value.split('')
  return Array(4)
    .fill('')
    .map((_, i) => digits[i] || '')
})

const focusHiddenInput = () => {
  hiddenInput.value?.focus()
}

const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  code.value = input.value.replace(/\D/g, '').slice(0, 4)
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const paste = event.clipboardData?.getData('text') || ''
  code.value = paste.replace(/\D/g, '').slice(0, 4)
}

const handleFocus = () => {
  isInputFocused.value = true
  handleClearError()
}

const handleClearError = () => {
  if (codeError.value) {
    code.value = ''
    codeError.value = false
  }
}

const handleVerify = async () => {
  if (loading.value) return

  try {
    loading.value = true
    if (code.value.length === 4) {
      const data = await otpApi.execute<'verify'>(
        otpApi.verify({
          code: code.value,
          ...(isPossibleNumber.value
            ? { userPhoneNumber: formattedContact.value?.e164 }
            : { userEmail: props.contact }),
          storeId: props.storeId,
        }),
      )
      emit('verified', data)
    }
  } catch (error) {
    const fetchError = otpApi.getError(error)
    if (fetchError?.statusCode === 401) {
      codeError.value = true
    } else if (fetchError?.statusCode === 404) {
      step.value = 'contact'
      toast.open({
        type: 'error',
        title: 'Error occurred',
        content:
          fetchError.statusCode === 404 && isPossibleNumber.value
            ? "Couldn't find your account. Try using your email address instead."
            : 'Could not find your user account. If you believe this is an error, contact support.',
      })
    } else {
      step.value = 'contact'
      toast.open({
        type: 'error',
        title: 'Error occurred',
        content:
          'An error occured while verifying your account. If the issue persists, contact support',
      })
    }
  } finally {
    loading.value = false
  }
}

const handleSendCode = async () => {
  if (loading.value || !isValidContact.value) {
    return
  }
  try {
    reset()
    handleClearError()
    loading.value = true

    const request = otpApi.start(
      isPossibleNumber.value
        ? {
            userPhoneNumber: formattedContact.value?.e164,
            storeId: props.storeId,
          }
        : { userEmail: props.contact, storeId: props.storeId },
    )
    await otpApi.execute<'start'>(request)
    step.value = 'code'
    timeout.value = timeoutDuration
    startTimer()
  } catch (error) {
    toast.open({
      type: 'error',
      title: 'Error occurred',
      content:
        'We encountered an error while attempting to send your login code. If the issue persists, contact support.',
    })
  } finally {
    loading.value = false
  }
}

const reset = () => {
  step.value = 'contact'
  code.value = ''
  codeError.value = false
  clearTimer()
}

onUnmounted(() => {
  step.value = 'contact'
  reset()
})

watch(
  () => code.value,
  () => {
    if (code.value.length === 4) {
      handleVerify()
    }
  },
)

const isInputFocused = ref<boolean>(false)

watch(
  () => step.value,
  (newStep) => {
    if (newStep === 'code') {
      nextTick(() => {
        setTimeout(() => {
          hiddenInput.value?.focus()
        }, 300)
      })
    }
  },
)

defineExpose({ loading, handleSendCode, reset, isValidContact })
</script>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.cursor-animation {
  animation: cursor-blink 1s step-end infinite;
}

@keyframes cursor-blink {
  50% {
    opacity: 0;
  }
}
</style>
